import { useEffect, useState } from 'react';
import {User, CreditCard, ExternalLink, CheckCircle, XCircle, Clock, AlertCircle} from 'lucide-react';
import {getMe, getMyRestaurants} from '../../api/directusClient'
// import './ProfilePage.css'; // Import your CSS file

// const BILLING = process.env.REACT_APP_BILLING_URL; // optional
// const CMS_URL = 'https://admin.nectrstudio.com/admin';
const API_URL = process.env.REACT_APP_DIRECTUS_URL;

export default function Account() {
    // const { user } = useAuth();
    const [user, setUser] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        getMe().then(setUser);
    }, []);

    useEffect(() => {
        const loadRestaurants = async () => {
            const data = await getMyRestaurants();
            console.log(data);
            setRestaurants(data);
        };
        loadRestaurants();
    }, []);

    const startCheckout = async (restaurantId) => {
        setBusy(prev => ({ ...prev, [restaurantId]: true }));

        try {
            const me = await getMe();

            const response = await fetch(`${API_URL}/stripe/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    restaurantId,
                    userId: me.id
                })
            });

            if (!response.ok) {
                throw new Error('Checkout failed');
            }

            const { url } = await response.json();
            window.location.href = url; // Redirects to Stripe checkout
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Failed to start checkout. Please try again.');
        } finally {
            setBusy(prev => ({ ...prev, [restaurantId]: false }));
        }
    };


    const openPortal = async (restaurantId) => {
        setBusy(prev => ({ ...prev, [restaurantId]: true }));

        try {
            // Refresh restaurant data first to get latest stripe_customer_id
            await getMyRestaurants();

            const restaurant = restaurants.find(r => r.id === restaurantId);

            if (!restaurant?.stripe_customer_id) {
                alert('No billing information found');
                return;
            }

            const response = await fetch(`${API_URL}/stripe/portal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    customerId: restaurant.stripe_customer_id
                })
            });

            if (!response.ok) throw new Error('Portal failed');

            const { url } = await response.json();
            window.location.href = url;
        } catch (error) {
            console.error('Portal error:', error);
            alert('Failed to open billing portal. Please try again.');
        } finally {
            setBusy(prev => ({ ...prev, [restaurantId]: false }));
        }
    };

    if (!user) {
        return (
            <div className="profile-container">
                <div className="profile-wrapper">
                    <div className="profile-loading">
                        <div className="loading-title"></div>
                        <div className="loading-subtitle"></div>
                        <div className="loading-content">
                            <div className="loading-block"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <div className="profile-wrapper">
                {/* Header */}
                <div className="profile-header">
                    <h1 className="profile-title">Your Profile</h1>
                    <p className="profile-subtitle">Manage your plan and open the CMS.</p>
                </div>


                <div className="restaurants-container">
                    {restaurants.map(restaurant => {
                        const status = restaurant.subscription_status || 'none';

                        const getStatusIcon = () => {
                            switch(status) {
                                case 'active': return <CheckCircle className="status-icon active" />;
                                case 'past_due': return <Clock className="status-icon past-due" />;
                                case 'canceling': return <AlertCircle className="status-icon canceling" />;
                                case 'canceled': return <XCircle className="status-icon canceled" />;
                                default: return <XCircle className="status-icon none" />;
                            }
                        };

                        const getStatusText = () => {
                            switch(status) {
                                case 'not_active': return "Not Active";
                                case 'active': return "Active";
                                case 'past_due': return "Past Due";
                                case 'canceling': return "Canceling";
                                case 'canceled': return "Canceled";
                                default: return "None";
                            }
                        };
                        const isBusy = busy[restaurant.id] || false;

                        return (
                            <div key={restaurant.id} className="plan-card">
                                <div className="plan-header">
                                    <div>
                                        <h2>{restaurant.name}</h2>
                                        <p>Subscription details</p>
                                    </div>
                                    <CreditCard className="plan-icon" />
                                </div>

                                <div className="plan-content">
                                    <div className="row">
                                        <div className="col-3">
                                            <label className="plan-label">Plan</label>
                                            <div className="plan-value">
                                                {restaurant.current_plan || '—'}
                                            </div>
                                        </div>

                                        <div className="col-3">
                                            <label className="plan-label">Status</label>
                                            <div className="status-container">
                                                {getStatusIcon()}
                                                <span className={`status-badge ${status}`}>
                                    {getStatusText()}
                                </span>
                                            </div>
                                        </div>


                                        <div className="col-3">
                                            <label className="plan-label">Actions</label>
                                            <div className="actions-container">
                                                {status === 'active' || status === 'canceling' ? (
                                                    <button
                                                        className="action-btn-secondary"
                                                        onClick={() => openPortal(restaurant.id, restaurant.stripe_customer_id)}
                                                        disabled={isBusy}
                                                    >
                                                        <CreditCard className="btn-icon" />
                                                        {isBusy ? 'Loading...' : 'Manage Billing'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="action-btn-primary"
                                                        onClick={() => startCheckout(restaurant.id)}
                                                        disabled={isBusy}
                                                    >
                                                        {isBusy ? 'Loading...' : 'Subscribe'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <label className="plan-label">Manage Business</label>
                                            <div className="actions-container">
                                                <a
                                                    className="btn-link"
                                                    href={`${API_URL}?restaurant=${restaurant.id}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <ExternalLink className="btn-icon" />
                                                    Open CMS
                                                </a>
                                            </div>
                                        </div>
                                </div>
                            </div>
                    </div>
                    );
                    })}
                </div>

                {/* Profile Information Card */}
                <div className="profile-info-card">
                    <div className="profile-info-header">
                        <User className="btn-icon" />
                        <h3>Profile Information</h3>
                    </div>

                    <div className="profile-info-content">
                        <div className="profile-info-grid">
                            <div className="plan-field">
                                <label className="plan-label">Email Address</label>
                                <div className="plan-value">{user?.email}</div>
                            </div>

                            <div className="plan-field">
                                <label className="plan-label">Full Name</label>
                                <div className="plan-value">
                                    {user?.first_name} {user?.last_name}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions">
                    <h4>Quick Actions</h4>
                    <p>Need help or want to make changes?</p>
                    <div className="quick-actions-buttons">
                        <button className="btn-quick teal" onClick={() => window.location.href = 'mailto:support@nectrstudio.com'}>
                            Contact Support
                        </button>
                        <a className="btn-quick pink" href={'docs'}>
                            View Documentation
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}